import React, { useState, useEffect, useCallback } from 'react';
import {
    TrendingUp, DollarSign, ShoppingCart, XCircle,
    RefreshCw, AlertCircle, BarChart3, Target, Users, Star, Package,
    AlertTriangle, CheckCircle
} from 'lucide-react';
import { getSalesPersonAnalyticsSummary } from '../../../../services/api/analyticsApi';
import {
    formatCurrency, formatNumber, buildDateRange,
    SkeletonBlock, ScoreCard, Section, StatusFunnel,
    TopCustomersTable, TopProductsTable, CustomDatePicker, CustomersToCallList
} from './analyticsShared';

// ─── Actionable Orders (Rejected / Needs Modification) ──────────────────────

const ActionableOrdersPanel = ({ data, loading }) => {
    if (loading) return <div className="space-y-2">{[...Array(3)].map((_, i) => <SkeletonBlock key={i} className="h-14" />)}</div>;

    if (!data?.count) {
        return (
            <div className="flex items-center gap-3 py-3 px-4 rounded-xl bg-emerald-50 border border-emerald-200">
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
                    <CheckCircle size={14} className="text-emerald-600" />
                </div>
                <div>
                    <p className="text-sm font-bold text-emerald-800">No orders need attention</p>
                    <p className="text-[11px] text-emerald-600 font-medium">All your orders are on track!</p>
                </div>
            </div>
        );
    }

    const statusConfig = {
        REJECTED: {
            label: 'Rejected',
            badge: 'bg-red-100 text-red-700 border border-red-200',
            icon: XCircle,
            iconColor: 'text-red-500',
        },
        REQUEST_FOR_MODIFICATION: {
            label: 'Needs Revision',
            badge: 'bg-amber-100 text-amber-700 border border-amber-200',
            icon: AlertTriangle,
            iconColor: 'text-amber-500',
        },
    };

    return (
        <div className="space-y-2.5">
            {data.orders.map(order => {
                const cfg = statusConfig[order.statusCode?.toUpperCase()] || {
                    label: order.statusCode, badge: 'bg-gray-100 text-gray-700', icon: AlertCircle, iconColor: 'text-gray-400'
                };
                const StatusIcon = cfg.icon;
                return (
                    <div key={order.publicId} className="flex items-center gap-3 p-3 rounded-xl bg-[var(--color-bg-hover)] border border-[var(--color-border-soft)] hover:border-[var(--color-border)] transition-all">
                        <div className="w-9 h-9 rounded-xl bg-[var(--color-bg-card)] border border-[var(--color-border-soft)] flex items-center justify-center shrink-0">
                            <StatusIcon size={15} className={cfg.iconColor} />
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-bold text-[var(--color-text-primary)] truncate">{order.customerName}</p>
                            <p className="text-[10px] text-[var(--color-text-muted)] font-mono">{order.publicId?.slice(-8)?.toUpperCase()}</p>
                        </div>
                        <div className="text-right shrink-0">
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${cfg.badge}`}>{cfg.label}</span>
                            <p className="text-xs font-black text-[var(--color-text-primary)] mt-0.5 tabular-nums">{formatCurrency(order.totalAmount)}</p>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

// ─── Main Component ──────────────────────────────────────────────────────────

const SalesPersonAnalyticsView = () => {
    const [filter, setFilter] = useState('today');
    const [customStart, setCustomStart] = useState(() => {
        const d = new Date(); d.setDate(d.getDate() - 29);
        return d.toISOString().split('T')[0];
    });
    const [customEnd, setCustomEnd] = useState(() => new Date().toISOString().split('T')[0]);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchAnalytics = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            let startDate, endDate;
            if (filter === 'custom') {
                startDate = new Date(customStart + 'T00:00:00Z').toISOString();
                endDate   = new Date(customEnd   + 'T23:59:59Z').toISOString();
            } else {
                const range = buildDateRange(filter);
                startDate = range.startDate;
                endDate   = range.endDate;
            }
            const result = await getSalesPersonAnalyticsSummary(startDate, endDate);
            setData(result);
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to load analytics. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [filter, customStart, customEnd]);

    useEffect(() => { fetchAnalytics(); }, [fetchAnalytics]);

    const filterOptions = [
        { id: 'today', label: 'Today' },
        { id: 'last7', label: 'Last 7 Days' },
        { id: 'custom', label: 'Date Range' },
    ];

    const cancellationRate = data?.kpi?.cancellationRate ?? null;
    const attentionCount = data?.ordersNeedingAttention?.count ?? 0;

    return (
        <div className="flex flex-col min-h-0 flex-1 overflow-y-auto custom-scrollbar">
            <div className="flex flex-col gap-4 max-w-[1280px] mx-auto w-full p-4 md:p-6 pb-10">

                {/* ── HEADER ──────────────────────────────────────────────────── */}
                <div className="flex flex-col sm:flex-row sm:items-start gap-3 justify-between flex-shrink-0">
                    <div>
                        <h1 className="text-xl md:text-2xl font-black text-[var(--color-text-primary)] flex items-center gap-2">
                            <BarChart3 size={22} className="text-violet-500" />
                            My Analytics
                        </h1>
                        <p className="text-xs text-[var(--color-text-muted)] mt-0.5 font-medium">
                            Your personal performance, based on your assigned and delegated orders
                        </p>
                    </div>

                    {/* Controls */}
                    <div className="flex items-center gap-2 flex-wrap">
                        <div className="flex bg-[var(--color-bg-hover)] rounded-xl p-1 border border-[var(--color-border)] gap-1">
                            {filterOptions.map(opt => (
                                <button
                                    key={opt.id}
                                    onClick={() => setFilter(opt.id)}
                                    className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${filter === opt.id
                                        ? 'bg-[var(--color-bg-card)] text-violet-600 shadow-sm border border-[var(--color-border)]'
                                        : 'text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]'
                                    }`}
                                >
                                    {opt.label}
                                </button>
                            ))}
                        </div>
                        <button
                            onClick={fetchAnalytics}
                            disabled={loading}
                            className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold border border-[var(--color-border)] bg-[var(--color-bg-card)] text-[var(--color-text-secondary)] hover:text-violet-600 hover:border-violet-300 hover:bg-violet-50 transition-all shadow-sm disabled:opacity-50"
                        >
                            <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
                            Refresh
                        </button>
                    </div>
                </div>

                {/* Date Range Picker */}
                {filter === 'custom' && (
                    <div className="flex-shrink-0">
                        <CustomDatePicker
                            customStart={customStart}
                            customEnd={customEnd}
                            onStartChange={setCustomStart}
                            onEndChange={setCustomEnd}
                        />
                    </div>
                )}

                {/* Error */}
                {error && (
                    <div className="flex items-center gap-3 px-4 py-3 rounded-2xl bg-rose-50 border border-rose-200 text-rose-800 text-sm font-medium flex-shrink-0">
                        <AlertCircle size={16} className="shrink-0 text-rose-500" />
                        {error}
                    </div>
                )}

                {/* ── SCORECARD ROW ─────────────────────────────────────────── */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 flex-shrink-0">
                    <ScoreCard
                        title="MY REVENUE"
                        value={formatCurrency(data?.kpi?.totalRevenue)}
                        subtitle="Delivered orders only"
                        icon={DollarSign}
                        gradient="bg-gradient-to-br from-violet-500 to-purple-600"
                        loading={loading}
                    />
                    <ScoreCard
                        title="MY ORDERS"
                        value={formatNumber(data?.kpi?.totalOrders)}
                        subtitle="All statuses in period"
                        icon={ShoppingCart}
                        gradient="bg-gradient-to-br from-blue-500 to-indigo-600"
                        loading={loading}
                    />
                    <ScoreCard
                        title="AVG. ORDER VALUE"
                        value={formatCurrency(data?.kpi?.averageOrderValue)}
                        subtitle="Per delivered order"
                        icon={TrendingUp}
                        gradient="bg-gradient-to-br from-emerald-500 to-teal-600"
                        loading={loading}
                    />
                    <ScoreCard
                        title="CANCELLATION RATE"
                        value={cancellationRate !== null ? `${Number(cancellationRate).toFixed(1)}%` : '—'}
                        subtitle="Cancelled & rejected"
                        icon={Target}
                        gradient={cancellationRate > 10
                            ? 'bg-gradient-to-br from-rose-500 to-red-600'
                            : 'bg-gradient-to-br from-amber-400 to-orange-500'}
                        loading={loading}
                    />
                </div>

                {/* ── ATTENTION BANNER (if there are orders needing action) ── */}
                {!loading && attentionCount > 0 && (
                    <div className="flex items-center gap-3 px-4 py-3 rounded-2xl bg-amber-50 border border-amber-300 flex-shrink-0">
                        <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                            <AlertTriangle size={15} className="text-amber-600" />
                        </div>
                        <div>
                            <p className="text-sm font-bold text-amber-900">
                                {attentionCount} order{attentionCount > 1 ? 's' : ''} require your immediate attention
                            </p>
                            <p className="text-[11px] text-amber-700 font-medium">
                                These are rejected or need modification — scroll down to review them.
                            </p>
                        </div>
                    </div>
                )}

                {/* ── 2-COL: NEEDS ATTENTION + TOP CUSTOMERS ─────────────── */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <Section
                        title="Orders Needing Attention"
                        icon={AlertTriangle}
                        iconColor="text-rose-500"
                        action={attentionCount > 0 && !loading && (
                            <span className="text-[10px] font-bold text-rose-700 bg-rose-100 px-2 py-1 rounded-full border border-rose-200">
                                {attentionCount} active
                            </span>
                        )}
                    >
                        <ActionableOrdersPanel data={data?.ordersNeedingAttention} loading={loading} />
                    </Section>

                    <Section
                        title="My Top Customers"
                        icon={Users}
                        iconColor="text-violet-500"
                        action={
                            <span className="text-[10px] font-bold text-[var(--color-text-muted)] bg-[var(--color-bg-hover)] px-2 py-1 rounded-full">
                                Top 5
                            </span>
                        }
                    >
                        <TopCustomersTable customers={data?.customerInsights?.topCustomers} loading={loading} />
                    </Section>
                </div>

                {/* ── ORDER PIPELINE / FUNNEL ───────────────────────────────── */}
                <Section title="My Order Pipeline" icon={ShoppingCart} iconColor="text-indigo-500">
                    <StatusFunnel funnel={data?.orderFunnel} loading={loading} />
                </Section>

                {/* ── CUSTOMERS TO CALL ─────────────────────────────────────── */}
                <Section
                    title="Customers to Follow Up"
                    icon={Users}
                    iconColor="text-rose-500"
                    action={
                        <span className="text-[10px] font-bold text-[var(--color-text-muted)] bg-[var(--color-bg-hover)] px-2 py-1 rounded-full">
                            No order in 30+ days
                        </span>
                    }
                >
                    <CustomersToCallList customers={data?.customerInsights?.customersToCall} loading={loading} />
                </Section>

                {/* ── MY TOP PRODUCTS ───────────────────────────────────────── */}
                <Section
                    title="My Top Selling Products"
                    icon={Star}
                    iconColor="text-amber-500"
                    action={
                        <span className="text-[10px] font-bold text-[var(--color-text-muted)] bg-[var(--color-bg-hover)] px-2 py-1 rounded-full">
                            By Qty
                        </span>
                    }
                >
                    <TopProductsTable products={data?.topProducts} loading={loading} />
                </Section>

            </div>
        </div>
    );
};

export default SalesPersonAnalyticsView;
